// Content container, port of the Next.js app's src/app/venue/venue-shell.tsx.
// Provides the page padding + max-width the ported pages assume. Admin pages
// get a dashboard-width container at lg+; most member pages stay mobile-app
// width at all sizes (native-app-mimicking); a small allowlist of rich pages
// (see shouldExpandOnDesktop) get a wider desktop container. Without this,
// ported pages (which use bare `space-y-6`) sit flush against the viewport edges.
import type { ReactNode } from "react";
import { useRouterState } from "@tanstack/react-router";
import { shouldExpandOnDesktop } from "../lib/venue-shell-decisions";

export function VenueShell({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isAdmin = pathname === "/admin" || pathname.startsWith("/admin/");
  const wideOnDesktop = !isAdmin && shouldExpandOnDesktop(pathname);
  // Admin (a management UI) stays comfortably inset. Member pages honour the
  // venue's density control via --app-pad (inset = 1rem, full-bleed = 0).
  // Wide-on-desktop pages (e.g. event detail) get a 6xl container at lg+ so
  // their 2-col layouts have real estate to breathe.
  let cls: string;
  if (isAdmin) {
    // Phone: centred app width (looks great as-is). Desktop: FULL BLEED,
    // the admin sidebar pins to the far-left viewport edge and the
    // content (e.g. the website builder) uses all the real estate.
    // Gutters are owned by AdminLayout, not this shell.
    cls = "max-w-lg px-4 lg:mx-0 lg:max-w-none lg:px-0";
  } else if (wideOnDesktop) {
    // Phone: same centred app width every other member page uses.
    // Desktop: expand to a comfortable 2-col-friendly width, add breathing
    // padding on the sides. Event detail pages use this — Fenton flagged
    // 2026-09-23 that a laptop viewport was showing a mobile-shaped column
    // in the middle of a black sea.
    cls = "max-w-lg px-[var(--app-pad,1rem)] lg:max-w-6xl lg:px-8";
  } else {
    cls = "max-w-lg px-[var(--app-pad,1rem)]";
  }
  return <div className={`mx-auto pb-24 pt-6 lg:pb-12 ${cls}`}>{children}</div>;
}
